/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  forwardRef,
  OnInit,
  Injector,
  input,
  signal,
  computed,
  output,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  FormsModule,
  Validators,
} from '@angular/forms';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-custom-input',
  standalone: true,
  imports: [FormsModule, CustomErrorDisplayComponent, CommonModule],
  templateUrl: './custom-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputComponent),
      multi: true,
    },
  ],
})
export class CustomInputComponent implements ControlValueAccessor, OnInit {
  label = input<string>('');
  type = input<string>('text');
  placeholder = input<string>('');
  hint = input<string | null>(null);
  iconLeft = input<string>('');
  iconRight = input<string>('');
  name = input<string>('');
  errors = input<{ [key: string]: string }>({});
  displayError = input<boolean>(true);
  onlyNumbers = input<boolean>(false);
  onlyIntegers = input<boolean>(false);
  clearable = input<boolean>(false);
  enterPressed = output<void>();
  inputBlur = output<void>();
  cleared = output<void>();
  disabled = input<boolean>(false);
  rows = input<number>(3);

  readonly effectiveName = computed(() => {
    const currentName = this.name();
    if (currentName) {
      return currentName;
    }
    const currentLabel = this.label();
    if (currentLabel) {
      return (
        currentLabel.toLowerCase().replace(/\s+/g, '-') +
        `-${Math.random().toString(36).substring(2, 7)}`
      );
    }
    return `custom-input-${Math.random().toString(36).substring(2, 9)}`;
  });

  _value = signal<any>(null);
  _formDisabled = signal(false);
  isDisabled = computed(() => this._formDisabled() || this.disabled());
  isFocused = false;
  ngControl: NgControl | null = null;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private _injector: Injector) {}

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;

      if (this.type() === 'tel' && this.ngControl.control) {
        this.ngControl.control.addValidators([
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern('^[0-9]*$'),
        ]);
        this.ngControl.control.updateValueAndValidity();
      }
    }
    // Name generation logic is now handled by the 'effectiveName' computed signal.
  }

  get isNumeric(): boolean {
    return this.onlyNumbers() || this.type() === 'tel';
  }

  get value(): any {
    return this._value();
  }

  set value(val: any) {
    if (this.isNumeric) {
      val = this._sanitizeNumericValue(val);
    }

    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(this._value());
    }
  }

  private _sanitizeNumericValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    let sanitized = value.toString();

    if (this.type() === 'tel') {
      return sanitized.replace(/[^0-9]/g, '');
    }

    // Solo enteros
    if (this.onlyIntegers()) {
      return sanitized.replace(/[^0-9]/g, '');
    }

    // dejar solo números y puntos
    sanitized = sanitized.replace(/[^0-9.]/g, '');

    // permitir un solo punto decimal
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    // limitar a 2 decimales
    const [integer, decimal] = sanitized.split('.');

    if (decimal !== undefined) {
      sanitized = `${integer}.${decimal.slice(0, 2)}`;
    }

    return sanitized;
  }

  isPasswordVisible = signal(false);

  readonly resolvedInputType = computed(() => {
    if (this.type() === 'password') {
      return this.isPasswordVisible() ? 'text' : 'password';
    }
    return this.type();
  });

  get passwordToggleIcon(): string {
    return this.isPasswordVisible() ? 'fa fa-eye' : 'fa fa-eye-slash';
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible.update((v) => !v);
  }

  writeValue(value: any): void {
    this._value.set(this.isNumeric ? this._sanitizeNumericValue(value) : value);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._formDisabled.set(isDisabled);
  }

  onValueChange(value: string): void {
    // The 'value' setter will handle updating _value, onChange, and onTouched
    this.value = value;
  }

  onKeyPress(event: KeyboardEvent): void {
    if (!this.isNumeric) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (this.onlyIntegers()) {
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
      return;
    }

    if (!/[\d.]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;

    // impedir segundo punto decimal
    if (event.key === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.isNumeric) {
      event.preventDefault();
      const clipboardData = event.clipboardData;
      const pastedText = clipboardData?.getData('text') || '';
      const input = event.target as HTMLInputElement;
      const nextValue =
        input.value.slice(0, input.selectionStart ?? 0) +
        pastedText +
        input.value.slice(input.selectionEnd ?? 0);
      const sanitized = this._sanitizeNumericValue(nextValue);
      this.value = sanitized;
      input.value = sanitized;
    }
  }

  onInputValueChange(event: Event): void {
    if (!this.isNumeric) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const sanitized = this._sanitizeNumericValue(input.value);

    if (input.value !== sanitized) {
      input.value = sanitized;
    }

    this.value = sanitized;
  }

  onInputFocus(): void {
    this.isFocused = true;
  }

  onInputBlur(): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit();
  }

  onClear(): void {
    this.value = '';
    this.cleared.emit();
  }

  onEnterKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.enterPressed.emit();
    }
  }
}
